import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOfferByChatId } from './useOfferByChatId';
import { useOpponentByChatId } from './useOpponentByChatId';
import { openChatActionModal } from '../actions/chatActionModal';
import { addChatToSelected, markChatsAsRead } from '../actions/chatList';
import { moveToArchive, removeFromArchive, removeFromSpam } from '../actions/chatOperations';
import { TMenuItem } from '../components/ChatList/ChatListContextMenu';
import {
  trackContextMenuDeleteClick,
  trackContextMenuMarkAsReadClick,
  trackContextMenuMoveToArchiveClick,
  trackContextMenuRemoveFromArchiveClick,
  trackContextMenuUnblockClick,
} from '../containers/ChatListContextMenuContainer/tracking';
import { chatListSelector } from '../selectors';
import { EChatAction } from '../types/chatActionModal';
import { ERoute } from '../types/route';

export const useChatListContextMenuActions = (): TMenuItem[] => {
  const dispatch = useDispatch();
  const { currentRoute } = useSelector(chatListSelector);

  const getOfferByChatId = useOfferByChatId();
  const getOpponentByChatId = useOpponentByChatId();

  return React.useMemo(() => {
    if (currentRoute === ERoute.Messages || currentRoute === ERoute.Unread) {
      return [
        {
          title: 'Перенести в архив',
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(moveToArchive(selectedChatId));
            trackContextMenuMoveToArchiveClick(offer, opponentId);
          },
        },
        {
          title: 'Отметить как прочитанное',
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(markChatsAsRead([selectedChatId]));
            trackContextMenuMarkAsReadClick(offer, opponentId);
          },
        },
        {
          title: 'Удалить',
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(addChatToSelected(selectedChatId));
            dispatch(
              openChatActionModal({
                buttonText: 'Удалить',
                text: 'Точно хотите удалить чат?',
                chatAction: EChatAction.Delete,
              }),
            );
            trackContextMenuDeleteClick(offer, opponentId);
          },
        },
      ];
    }

    if (currentRoute === ERoute.Spam) {
      return [
        {
          title: 'Разблокировать',
          handler: (selectedChatId: string) => {
            const offer = getOfferByChatId(selectedChatId);
            const opponentId = getOpponentByChatId(selectedChatId);

            dispatch(removeFromSpam(selectedChatId));
            trackContextMenuUnblockClick(offer, opponentId);
          },
        },
      ];
    }

    return [
      {
        title: 'Вернуть',
        handler: (selectedChatId: string) => {
          const offer = getOfferByChatId(selectedChatId);
          const opponentId = getOpponentByChatId(selectedChatId);

          dispatch(removeFromArchive(selectedChatId));
          trackContextMenuRemoveFromArchiveClick(offer, opponentId);
        },
      },
      {
        title: 'Удалить',
        handler: (selectedChatId: string) => {
          const offer = getOfferByChatId(selectedChatId);
          const opponentId = getOpponentByChatId(selectedChatId);

          dispatch(addChatToSelected(selectedChatId));
          dispatch(
            openChatActionModal({
              buttonText: 'Удалить',
              text: 'Точно хотите удалить чат?',
              chatAction: EChatAction.Delete,
            }),
          );
          trackContextMenuDeleteClick(offer, opponentId);
        },
      },
    ];
  }, [currentRoute, getOfferByChatId, getOpponentByChatId, dispatch]);
};
